// The pill at the bottom of the screen: why the last click did nothing, and
// what a drag would do (PLAN-UI §28.3's bottom-centre row, §28.9, U7;
// screen-mock `#pill`).
//
// **It replaced `ShelfStrip`'s caption line, and the ellipsis is the reason.**
// That line was 340px of `nowrap` with `text-overflow: ellipsis`, so the
// longest refusals — the ones that name a rule rather than a state, which are
// exactly the ones a player cannot guess — arrived cut in half. A refusal is
// said out loud *or it is not said*: half a reason is the silent no-op the
// refusal machinery exists to replace (§28.6). `HintPill` wraps at 70vw and
// carries no clamp of any kind; nothing here adds one.
//
// **It is never a control.** `pointer-events: none`, so it cannot swallow a
// click meant for the board it floats over — the U6 hover-card ruling counted
// once more, and the same reason `PieceOverlays` takes no pointer.
//
// **Quiet is hidden, not merely transparent.** An `opacity: 0` element still
// occupies its box, and a box across the bottom-centre of the board is where
// the player's own pieces are; `visibility` transitions discretely, so it
// flips to `hidden` at the *end* of the fade out and back to `visible` at the
// start of the fade in, which is the behaviour wanted from one property.
//
// Timing is `pill.ts` and is tested there. This file is the DOM and one timer.

import { useEffect, useRef, useState } from "react";

import { HintPill, motion, styled, useEdgeInsets } from "./kit";
import { pillStep, QUIET } from "./pill";
import { TOOL_HINT, type Tool } from "./tool";

/**
 * Clear of whatever is at the bottom edge — the hand, or nothing.
 *
 * 16 rather than 12 since U7's review: the pill grew, the hand's cards rise to
 * meet it, and 12px between two loud surfaces read as a collision.
 */
const GAP = 16;

/**
 * Fixed, bottom-centre, above the board and under the modal ladder.
 *
 * `z.pill` is its own rung for this: it has to read over the board and over
 * the hand's cards, and it must not sit over a banner or the pause menu, both
 * of which are moments that have asked for the screen.
 */
const Perch = styled("div")(({ theme }) => ({
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: theme.kit.z.pill,
  // A sentence about the board, never a surface on it.
  pointerEvents: "none",
  display: "flex",
  justifyContent: "center",
  ...motion([], ["opacity", "visibility"], theme.kit.motion.duration.draw)
}));

export interface RefusalPillProps {
  /** The live refusal, or null. `SessionView.refusal.text`. */
  refusal: string | null;
  /**
   * How many refusals have been said — `SessionView.refusal.said`.
   *
   * The counter and not the text is what identifies an announcement, so the
   * same sentence refused twice restarts the clock instead of being swallowed
   * as a re-render. See `pill.ts`.
   */
  said: number;
  /** The live drag tool. A change announces its sentence once. */
  tool: Tool;
  /**
   * How high above the bottom edge the pill must stand, whatever the registered
   * inset says. `HAND_REACH` while a hand is drawn, 0 otherwise.
   *
   * **A zone registers its *resting* depth, and a card rises past it.** The
   * inset is what the camera is told is covered, which is the fan at rest; a
   * card lifted under the pointer reaches nearly a hundred pixels higher and
   * covered the pill outright. So the perch takes the larger of the two — the
   * inset still wins when something deeper than the hand is docked down there.
   */
  clear: number;
  /**
   * Something the game has to say that nobody clicked for (§29 U5), or null.
   *
   * **Latched on the text**, so a shell that re-renders forty times a second
   * says it once. Today it is only the continued run whose history could not
   * be verified — a fact about the session, not a refusal, so it is not rust.
   */
  notice?: string | null;
}

export function RefusalPill({ refusal, said, tool, clear, notice }: RefusalPillProps) {
  const [pill, setPill] = useState(QUIET);
  const insets = useEdgeInsets();
  /**
   * The last thing said, kept for the length of the fade out.
   *
   * The element stays mounted so it can fade, and a fading pill still has to
   * have words in it — clearing the text on the same frame the opacity starts
   * falling is a blank pill shrinking away.
   */
  const shown = useRef({ text: "", bad: false, id: "tool-hint" });
  // All three, or a rust refusal turns grey as it fades: `pillStep`'s tick
  // resets `bad` with the text, and a pill changing colour on its way out is
  // a second announcement nobody made.
  if (pill.text !== null) {
    shown.current = {
      text: pill.text,
      bad: pill.bad,
      // **`notice` is a third id, and the two that existed are unmoved**
      // (§28.0: test ids are API) — a smoke reading `refusal` or `tool-hint`
      // still reads exactly what it did.
      id: pill.bad ? "refusal" : pill.text === pill.notice ? "notice" : "tool-hint"
    };
  }

  useEffect(() => {
    if (refusal === null) return;
    setPill((was) => pillStep(was, { kind: "refusal", text: refusal, said, now: Date.now() }));
  }, [refusal, said]);

  useEffect(() => {
    const text = TOOL_HINT[tool] ?? TOOL_HINT.turn;
    setPill((was) => pillStep(was, { kind: "tool", tool, text, now: Date.now() }));
  }, [tool]);

  // **After the tool effect, deliberately.** Both fire on mount, and the tool
  // one records the opening tool without saying anything — so the notice is
  // the first line the pill ever raises, which is what a Continue that lost
  // its history needs.
  useEffect(() => {
    if (!notice) return;
    setPill((was) => pillStep(was, { kind: "notice", text: notice, now: Date.now() }));
  }, [notice]);

  // **One timer, and the model decides.** It is scheduled off `until` rather
  // than off a duration, so a line replaced mid-window is one timer replaced
  // rather than two racing — and `pillStep`'s `tick` re-reads the window, so
  // even a fire that escapes the cleanup cannot blank a fresher sentence.
  useEffect(() => {
    if (pill.text === null) return;
    const id = window.setTimeout(
      () => setPill((was) => pillStep(was, { kind: "tick", now: Date.now() })),
      Math.max(0, pill.until - Date.now())
    );
    return () => window.clearTimeout(id);
  }, [pill.text, pill.until]);

  const saying = pill.text !== null;
  return (
    <Perch
      // **The two ids the strip's caption line carried**, because test ids are
      // API (§28.0): a smoke that read `refusal` after a click still reads it,
      // and reads the whole sentence now rather than 340px of it. None is
      // on the element while it is quiet, so "nothing was refused" is
      // findable as the absence it is.
      data-testid={saying ? shown.current.id : undefined}
      aria-live="polite"
      style={{
        bottom: Math.max(insets.bottom, clear) + GAP,
        opacity: saying ? 1 : 0,
        visibility: saying ? "visible" : "hidden"
      }}
    >
      {/* **Keyed on the announcement, so the entrance replays.** A CSS
          animation runs once per mount and a re-render is not one, so the same
          sentence refused twice would arrive with no pop at all — the case the
          counter exists for. `spoke` bumps only when a line is actually
          raised, so a pill part-way through its fade *out* is never remounted
          blank underneath `shown`. */}
      <HintPill key={pill.spoke} bad={shown.current.bad}>
        {shown.current.text}
      </HintPill>
    </Perch>
  );
}
